/**
 * Service de gestion du cache pour les requêtes API
 * Implémente différentes stratégies de cache selon le type de données
 */

class CacheService {
  constructor() {
    // Cache en mémoire (Map pour performance optimale)
    this.cache = new Map();
    
    // Durées de vie par défaut (en millisecondes)
    this.TTL = {
      SHORT: 1 * 60 * 1000,      // 1 minute - Données très volatiles
      MEDIUM: 5 * 60 * 1000,     // 5 minutes - Données modérément volatiles
      LONG: 15 * 60 * 1000,      // 15 minutes - Données peu volatiles
      VERY_LONG: 60 * 60 * 1000, // 1 heure - Données quasi-statiques
      SESSION: Infinity,          // Toute la session - Données statiques
    };

    // Configuration des stratégies par endpoint
    this.strategies = {
      // Données statiques (rarement modifiées) - PERSISTÉES
      'role/all': { ttl: this.TTL.SESSION, strategy: 'cache-first', persist: true },
      'reference/': { ttl: this.TTL.VERY_LONG, strategy: 'cache-first', persist: true },
      'fonctionnalite/role/': { ttl: this.TTL.VERY_LONG, strategy: 'cache-first', persist: true },
      'pays/': { ttl: this.TTL.SESSION, strategy: 'cache-first', persist: true }, // Liste des pays (chargée une fois par session)
      'villes/': { ttl: this.TTL.SESSION, strategy: 'cache-first', persist: true }, // Liste des villes (chargée une fois par session)
      
      // Données semi-statiques (modifiées occasionnellement) - PERSISTÉES
      'offreVoyage/all': { ttl: this.TTL.LONG, strategy: 'cache-first', persist: true },
      'offreVoyages/getTravelOfferByCompagnieTransport': { ttl: this.TTL.LONG, strategy: 'cache-first', persist: true },
      'gareCompagnieTransport/': { ttl: this.TTL.LONG, strategy: 'cache-first', persist: true },
      'gares/': { ttl: this.TTL.SESSION, strategy: 'cache-first', persist: true }, // Chargé une seule fois par session
      'bus/compagnie/': { ttl: this.TTL.LONG, strategy: 'cache-first', persist: true },
      'document/': { ttl: this.TTL.VERY_LONG, strategy: 'cache-first', persist: true },
      'utilisateur/': { ttl: this.TTL.LONG, strategy: 'cache-first', persist: true },
      
      // Données dynamiques (modifiées fréquemment) - PERSISTÉES pour meilleure UX
      'reservationBilletVoyages/': { ttl: this.TTL.MEDIUM, strategy: 'cache-first', persist: true },
      'programme/': { ttl: this.TTL.MEDIUM, strategy: 'cache-first', persist: true },
      
      // Données temps réel (très volatiles) - RAM UNIQUEMENT
      'statistiques/': { ttl: this.TTL.SHORT, strategy: 'network-first', persist: false },
      'bagage/': { ttl: this.TTL.SHORT, strategy: 'network-first', persist: false },
    };

    // Préfixe pour les clés sessionStorage
    this.STORAGE_PREFIX = 'api_cache_';

    // Restaurer le cache depuis sessionStorage au démarrage
    this.restoreFromStorage();
  }

  /**
   * Génère une clé de cache unique basée sur l'endpoint et les paramètres
   */
  generateKey(endpoint, data = {}, config = {}) {
    const params = JSON.stringify({ endpoint, data, config });
    return `cache_${this._hashCode(params)}`;
  }

  /**
   * Hash simple pour générer des clés courtes
   */
  _hashCode(str) {
    let hash = 0;
    for (let i = 0; i < str.length; i++) {
      const char = str.charCodeAt(i);
      hash = ((hash << 5) - hash) + char;
      hash = hash & hash; // Convert to 32bit integer
    }
    return Math.abs(hash).toString(36);
  }

  /**
   * Détermine la stratégie de cache pour un endpoint
   */
  getStrategy(endpoint) {
    // Chercher la stratégie correspondante
    for (const [pattern, config] of Object.entries(this.strategies)) {
      if (endpoint.includes(pattern)) {
        return config;
      }
    }
    
    // Stratégie par défaut : cache court avec network-first, pas de persistance
    return { ttl: this.TTL.MEDIUM, strategy: 'network-first', persist: false };
  }

  /**
   * Restaure le cache depuis sessionStorage au démarrage
   */
  restoreFromStorage() {
    try {
      let restoredCount = 0;
      const now = Date.now();

      // Parcourir toutes les clés de sessionStorage
      for (let i = 0; i < sessionStorage.length; i++) {
        const key = sessionStorage.key(i);
        
        // Vérifier si c'est une clé de cache
        if (key && key.startsWith(this.STORAGE_PREFIX)) {
          try {
            const stored = sessionStorage.getItem(key);
            if (stored) {
              const entry = JSON.parse(stored);
              
              // Vérifier si l'entrée est encore valide
              const age = now - entry.timestamp;
              if (age < entry.ttl) {
                // Restaurer en RAM
                const cacheKey = key.replace(this.STORAGE_PREFIX, '');
                this.cache.set(cacheKey, entry);
                restoredCount++;
              } else {
                // Supprimer les entrées expirées
                sessionStorage.removeItem(key);
              }
            }
          } catch (e) {
            // Supprimer les entrées corrompues
            sessionStorage.removeItem(key);
          }
        }
      }

      if (restoredCount > 0) {
        console.log(`✅ Cache restauré: ${restoredCount} entrées depuis sessionStorage`);
      }
    } catch (e) {
      console.error('❌ Erreur lors de la restauration du cache:', e);
    }
  }

  /**
   * Sauvegarde une entrée dans sessionStorage si nécessaire
   */
  saveToStorage(key, entry, shouldPersist) {
    if (!shouldPersist) return;

    try {
      const storageKey = this.STORAGE_PREFIX + key;
      sessionStorage.setItem(storageKey, JSON.stringify(entry));
    } catch (e) {
      // sessionStorage plein ou autre erreur
      if (e.name === 'QuotaExceededError') {
        console.warn('⚠️ sessionStorage plein, nettoyage des anciennes entrées...');
        this.cleanupStorage();
        // Réessayer
        try {
          const storageKey = this.STORAGE_PREFIX + key;
          sessionStorage.setItem(storageKey, JSON.stringify(entry));
        } catch (e2) {
          console.error('❌ Impossible de sauvegarder en sessionStorage:', e2);
        }
      }
    }
  }

  /**
   * Nettoie les entrées expirées du sessionStorage
   */
  cleanupStorage() {
    try {
      const now = Date.now();
      const keysToRemove = [];

      for (let i = 0; i < sessionStorage.length; i++) {
        const key = sessionStorage.key(i);
        
        if (key && key.startsWith(this.STORAGE_PREFIX)) {
          try {
            const stored = sessionStorage.getItem(key);
            if (stored) {
              const entry = JSON.parse(stored);
              const age = now - entry.timestamp;
              
              if (age >= entry.ttl) {
                keysToRemove.push(key);
              }
            }
          } catch (e) {
            keysToRemove.push(key);
          }
        }
      }

      keysToRemove.forEach(key => sessionStorage.removeItem(key));
      
      if (keysToRemove.length > 0) {
        console.log(`🧹 sessionStorage nettoyé: ${keysToRemove.length} entrées expirées`);
      }
    } catch (e) {
      console.error('❌ Erreur lors du nettoyage du sessionStorage:', e);
    }
  }

  /**
   * Vérifie si une entrée de cache est encore valide
   */
  isValid(cacheEntry) {
    if (!cacheEntry) return false;
    
    const now = Date.now();
    const age = now - cacheEntry.timestamp;
    
    return age < cacheEntry.ttl;
  }

  /**
   * Récupère une valeur du cache
   */
  get(key) {
    const entry = this.cache.get(key);
    
    if (!entry) {
      return null;
    }
    
    if (!this.isValid(entry)) {
      this.cache.delete(key);
      return null;
    }
    
    console.log(`✅ Cache HIT: ${key}`);
    return entry.data;
  }

  /**
   * Stocke une valeur dans le cache (RAM + sessionStorage si persist=true)
   */
  set(key, data, ttl = this.TTL.MEDIUM, persist = false) {
    const entry = {
      data,
      timestamp: Date.now(),
      ttl,
    };
    
    // Toujours stocker en RAM
    this.cache.set(key, entry);
    
    // Sauvegarder dans sessionStorage si demandé
    if (persist) {
      this.saveToStorage(key, entry, true);
      console.log(`💾 Cache SET: ${key} (TTL: ${ttl / 1000}s) [RAM + sessionStorage]`);
    } else {
      console.log(`💾 Cache SET: ${key} (TTL: ${ttl / 1000}s) [RAM only]`);
    }
  }

  /**
   * Invalide une entrée de cache (RAM + sessionStorage)
   */
  invalidate(key) {
    let removed = false;
    
    // Supprimer de RAM
    if (this.cache.has(key)) {
      this.cache.delete(key);
      removed = true;
    }
    
    // Supprimer de sessionStorage
    const storageKey = this.STORAGE_PREFIX + key;
    if (sessionStorage.getItem(storageKey)) {
      sessionStorage.removeItem(storageKey);
      removed = true;
    }
    
    if (removed) {
      console.log(`🗑️ Cache INVALIDATE: ${key}`);
    }
    return removed;
  }

  /**
   * Invalide toutes les entrées correspondant à un pattern (RAM + sessionStorage)
   */
  invalidatePattern(pattern) {
    let count = 0;
    
    // Supprimer de RAM
    for (const key of this.cache.keys()) {
      if (key.includes(pattern)) {
        this.cache.delete(key);
        count++;
      }
    }
    
    // Supprimer de sessionStorage
    const keysToRemove = [];
    for (let i = 0; i < sessionStorage.length; i++) {
      const key = sessionStorage.key(i);
      if (key && key.startsWith(this.STORAGE_PREFIX) && key.includes(pattern)) {
        keysToRemove.push(key);
      }
    }
    keysToRemove.forEach(key => {
      sessionStorage.removeItem(key);
      count++;
    });
    
    console.log(`🗑️ Cache INVALIDATE PATTERN: ${pattern} (${count} entrées)`);
    return count;
  }

  /**
   * Vide complètement le cache (RAM + sessionStorage)
   */
  clear() {
    const ramSize = this.cache.size;
    this.cache.clear();
    
    // Supprimer toutes les entrées de cache du sessionStorage
    const keysToRemove = [];
    for (let i = 0; i < sessionStorage.length; i++) {
      const key = sessionStorage.key(i);
      if (key && key.startsWith(this.STORAGE_PREFIX)) {
        keysToRemove.push(key);
      }
    }
    keysToRemove.forEach(key => sessionStorage.removeItem(key));
    
    const totalSize = ramSize + keysToRemove.length;
    console.log(`🗑️ Cache CLEAR: ${totalSize} entrées supprimées (${ramSize} RAM + ${keysToRemove.length} sessionStorage)`);
  }

  /**
   * Nettoie les entrées expirées (RAM + sessionStorage)
   */
  cleanup() {
    let ramCount = 0;
    
    // Nettoyer RAM
    for (const [key, entry] of this.cache.entries()) {
      if (!this.isValid(entry)) {
        this.cache.delete(key);
        ramCount++;
      }
    }
    
    // Nettoyer sessionStorage
    this.cleanupStorage();
    
    if (ramCount > 0) {
      console.log(`🧹 Cache CLEANUP: ${ramCount} entrées expirées supprimées de RAM`);
    }
    return ramCount;
  }

  /**
   * Obtient les statistiques du cache (RAM + sessionStorage)
   */
  getStats() {
    const now = Date.now();
    let ramValid = 0;
    let ramExpired = 0;
    let ramSize = 0;
    let storageValid = 0;
    let storageExpired = 0;
    let storageSize = 0;

    // Stats RAM
    for (const entry of this.cache.values()) {
      if (this.isValid(entry)) {
        ramValid++;
      } else {
        ramExpired++;
      }
      ramSize += JSON.stringify(entry.data).length;
    }

    // Stats sessionStorage
    for (let i = 0; i < sessionStorage.length; i++) {
      const key = sessionStorage.key(i);
      if (key && key.startsWith(this.STORAGE_PREFIX)) {
        try {
          const stored = sessionStorage.getItem(key);
          if (stored) {
            const entry = JSON.parse(stored);
            const age = now - entry.timestamp;
            
            if (age < entry.ttl) {
              storageValid++;
            } else {
              storageExpired++;
            }
            storageSize += stored.length;
          }
        } catch (e) {
          storageExpired++;
        }
      }
    }

    return {
      ram: {
        total: this.cache.size,
        valid: ramValid,
        expired: ramExpired,
        sizeKB: (ramSize / 1024).toFixed(2),
      },
      sessionStorage: {
        total: storageValid + storageExpired,
        valid: storageValid,
        expired: storageExpired,
        sizeKB: (storageSize / 1024).toFixed(2),
      },
      combined: {
        total: this.cache.size + storageValid + storageExpired,
        valid: ramValid + storageValid,
        expired: ramExpired + storageExpired,
        sizeKB: ((ramSize + storageSize) / 1024).toFixed(2),
      }
    };
  }

  /**
   * Stratégie Cache-First : Retourne le cache si disponible, sinon fait la requête
   */
  async cacheFirst(key, fetchFunction, ttl, persist) {
    // Vérifier le cache d'abord
    const cached = this.get(key);
    if (cached !== null) {
      return cached;
    }

    // Pas de cache, faire la requête
    console.log(`🌐 Cache MISS: ${key} - Fetching from network`);
    const data = await fetchFunction();
    this.set(key, data, ttl, persist);
    return data;
  }

  /**
   * Stratégie Network-First : Fait la requête d'abord, utilise le cache en fallback
   */
  async networkFirst(key, fetchFunction, ttl, persist) {
    try {
      // Essayer la requête réseau d'abord
      console.log(`🌐 Network FIRST: ${key} - Fetching from network`);
      const data = await fetchFunction();
      this.set(key, data, ttl, persist);
      return data;
    } catch (error) {
      // En cas d'erreur, utiliser le cache même expiré
      const cached = this.cache.get(key);
      if (cached) {
        console.warn(`⚠️ Network FAILED: ${key} - Using stale cache`);
        return cached.data;
      }
      throw error;
    }
  }

  /**
   * Stratégie Stale-While-Revalidate : Retourne le cache et met à jour en arrière-plan
   */
  async staleWhileRevalidate(key, fetchFunction, ttl, persist) {
    const cached = this.get(key);
    
    // Si cache valide, le retourner et rafraîchir en arrière-plan
    if (cached !== null) {
      // Rafraîchir en arrière-plan (sans attendre)
      fetchFunction()
        .then(data => this.set(key, data, ttl, persist))
        .catch(err => console.error(`Background refresh failed for ${key}:`, err));
      
      return cached;
    }

    // Pas de cache, faire la requête
    console.log(`🌐 Cache MISS: ${key} - Fetching from network`);
    const data = await fetchFunction();
    this.set(key, data, ttl, persist);
    return data;
  }

  /**
   * Méthode principale pour exécuter une requête avec cache
   */
  async execute(endpoint, fetchFunction, data = {}, config = {}) {
    const key = this.generateKey(endpoint, data, config);
    const { ttl, strategy, persist } = this.getStrategy(endpoint);

    switch (strategy) {
      case 'cache-first':
        return await this.cacheFirst(key, fetchFunction, ttl, persist);
      
      case 'network-first':
        return await this.networkFirst(key, fetchFunction, ttl, persist);
      
      case 'stale-while-revalidate':
        return await this.staleWhileRevalidate(key, fetchFunction, ttl, persist);
      
      default:
        return await this.networkFirst(key, fetchFunction, ttl, persist);
    }
  }
}

// Créer une instance singleton
const cacheService = new CacheService();

// Nettoyer le cache toutes les 5 minutes
setInterval(() => {
  cacheService.cleanup();
}, 5 * 60 * 1000);

export default cacheService;
